import * as RadixPopover from "@radix-ui/react-popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import { Command } from "cmdk";
import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { Checkbox } from "../../checkbox/Checkbox";
import { Divider } from "../../divider/Divider";
import { FieldError } from "../../fieldError/FieldError";
import { Icon } from "../../icon/Icon";
import { Note } from "../../note/Note";
import { useBodyScrollLock } from "../common/hooks/useBodyScrollLock";
import { useFilteredOptions } from "../common/hooks/useFilteredOption";
import { useSelectKeyboard } from "../common/hooks/useSelectKeyboard";
import * as labeledStyles from "../common/LabeledTrigger.css";
import { LabeledTriggerValue } from "../common/LabeledTriggerValue";
import * as selectStyles from "../common/Select.css";
import { Option, SelectSize } from "../common/SelectBase";
import { SelectHeaderActions } from "./components/SelectHeaderActions";
import { useVirtualListAutoMeasure } from "./hooks/useVirtualListAutoMeasure";
import { MultiTriggerValue } from "./MultiTriggerValue";
import {
  formatPasteReport,
  isListPaste,
  matchPastedEntries,
  splitPastedList
} from "./pasteList";
import { SelectFooterActions } from "./SelectFooterActions";

/**
 * Default texts
 */
const LOADING_TEXT = "Loading...";
const SEARCH_TEXT = "Search...";
const NO_RESULTS_TEXT = "No results found.";

const VIEWPORT_HEIGHT = 800;

export interface MultiSelectProps {
  /**
   *  Controlled value - array of selected option values
   */
  options: Option[];
  /**
   *  Controlled selected values
   */
  value?: string[];
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the select is disabled. Default: false
   */
  disabled?: boolean;
  /**
   * Whether the select is searchable.
   * Default: true
   */
  searchable?: boolean;
  /**
   * Position of the dropdown relative to the trigger. Default:
   * "bottom" */
  position?: "top" | "bottom" | "left" | "right";
  /**
   * Whether the select is in a loading state.
   * Default: false
   */
  loading?: boolean;

  /**
   * Handler called when selection changes
   */
  onChange: (value: string[]) => void;
  /**
   * The size variant of the select.
   */
  size?: SelectSize;
  /**
   * Error message to display below the select.
   */
  error?: string | boolean;
  /**
   * Enable keyboard-driven type-to-search: typing on the focused trigger
   * auto-opens the popover and seeds search. Tab commits the active match,
   * closes the popover, and moves focus forward.
   */
  typeToSearch?: boolean;
  /**
   * Filter-bar variant. When set, the trigger displays the label as an
   * eyebrow above the selected value(s) — keeping the filter name visible
   * even when several filters sit side-by-side. Also drives the trigger's
   * `aria-label`.
   */
  label?: string;
  /**
   * Same rule as `SelectBase`'s `CommonSelectProps.modalPopover`.
   */
  modalPopover?: boolean;
  /**
   * Action icons rendered inside the trigger, between value and chevron
   * (mirrors SingleSelect's slot).
   */
  triggerActions?: React.ReactNode;
  /**
   * Content rendered below the option list (e.g. a "New version" action).
   * Replaces the default Clear/Close footer — the trigger's clear icon and
   * Escape/click-away keep both behaviours reachable. The function form
   * receives a callback that closes the popover.
   */
  footerContent?: React.ReactNode | ((close: () => void) => React.ReactNode);
}

export function MultiSelect({
  options,
  value = [],
  placeholder,
  disabled,
  loading = false,
  position = "bottom",
  searchable = true,
  onChange,
  size = "sm",
  error,
  typeToSearch = false,
  label,
  modalPopover = false,
  triggerActions,
  footerContent
}: MultiSelectProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pasteReport, setPasteReport] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const optionListId = useId();

  /**
   * hooks: filtering + row size estimation
   */
  const filteredOptions = useFilteredOptions(options, search);

  /**
   * Group relevant options before non-relevant ones. `Array.sort` is stable,
   * so each group keeps its original order. This guarantees the
   * relevant/non-relevant divider is a single boundary instead of one divider
   * per interleaved transition (which happens when a consumer marks scattered
   * options — e.g. still-selected ones — as relevant).
   */
  const sortedOptions = useMemo(
    () =>
      [...filteredOptions].sort(
        (a, b) => Number(Boolean(b.relevant)) - Number(Boolean(a.relevant))
      ),
    [filteredOptions]
  );

  const showSelectHeaderActions = sortedOptions.length > 0 && !loading;

  const relevantCount = sortedOptions.filter((opt) => opt.relevant).length;
  /**
   * Draw exactly one divider, after the last relevant row — but only when
   * both a relevant and a non-relevant group exist. `-1` means no divider.
   */
  const dividerIndex =
    relevantCount > 0 && relevantCount < sortedOptions.length
      ? relevantCount - 1
      : -1;

  /**
   * virtualizer
   */
  const virtualizer = useVirtualizer({
    count: sortedOptions.length,
    overscan: 5,
    getScrollElement: () => listRef.current,
    estimateSize: () => 45,
    getItemKey: (index) => sortedOptions[index]?.value ?? index,
    enabled: true
  });

  const virtualItems = virtualizer.getVirtualItems();

  /**
   * lock page scroll while open
   */
  useBodyScrollLock(isPopoverOpen);

  useVirtualListAutoMeasure({
    isPopoverOpen,
    virtualizer,
    filteredOptions: sortedOptions
  });

  /**
   * toggle one option (controlled)
   */

  const toggleOption = useCallback(
    (optionValue: string) => {
      // A locked option keeps whatever state it is in: the consumer does not
      // own the selection, so accepting the click and reverting it is what the
      // lock exists to avoid.
      if (options.find((o) => o.value === optionValue)?.disabled) {
        return;
      }
      const next = value?.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...(value || []), optionValue];
      onChange(next);
    },
    [onChange, options, value]
  );

  function handleClear() {
    // Clear-all is still a deselect, so the locked ones stay — otherwise the
    // one control the lock does not cover removes exactly what it protects.
    onChange(
      value.filter((v) => options.find((o) => o.value === v)?.disabled ?? false)
    );
  }

  function handleTogglePopover() {
    setIsPopoverOpen((prev) => !prev);
    setSearch("");
    setPasteReport(null);
  }

  function handleOpenChange(open: boolean) {
    setIsPopoverOpen(open);
    if (!open) {
      setPasteReport(null);
    }
  }

  /**
   * List paste (Excel column/row): select every entry that matches an option.
   * Single-line, tab-free pastes fall through to the search input untouched.
   */
  function handleSearchPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!isListPaste(text)) {
      return;
    }
    event.preventDefault();
    const result = matchPastedEntries(splitPastedList(text), options);
    onChange(Array.from(new Set([...value, ...result.matchedValues])));
    setSearch("");
    setPasteReport(formatPasteReport(result));
  }

  // Shared keyboard model: Arrow/Home/End navigation, Enter/Space to toggle the
  // active row (kept open for multi-select), Tab to commit-and-close.
  const { activeIndex, activeOption, handleKeyDown } = useSelectKeyboard({
    options: sortedOptions,
    search,
    isOpen: isPopoverOpen,
    typeToSearch,
    disabled: loading,
    scrollToIndex: (index) => virtualizer.scrollToIndex(index),
    onSelect: (option) => toggleOption(option.value),
    onTab: (option) => {
      if (!value.includes(option.value)) toggleOption(option.value);
    },
    onClose: () => {
      handleOpenChange(false);
      setSearch("");
    }
  });

  /** Type-to-search: typing on the trigger auto-opens and seeds search. */
  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleTogglePopover();
      return;
    }
    if (
      typeToSearch &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      setSearch(event.key);
      setIsPopoverOpen(true);
    }
  }

  const viewPortStyle = {
    height:
      !loading || sortedOptions.length === 0 ? "auto" : `${VIEWPORT_HEIGHT}px`,
    overflow: !loading || sortedOptions.length === 0 ? "visible" : "auto",
    position: "relative",
    width: "100%"
  } as React.CSSProperties;

  const isLabeled = label !== undefined;
  const isActive = value.length > 0;
  const resolvedFooter =
    typeof footerContent === "function"
      ? footerContent(() => setIsPopoverOpen(false))
      : footerContent;
  const triggerAriaLabel = label ?? placeholder ?? "Select options";
  const activeDescendantId = activeOption
    ? `${optionListId}-${activeOption.value}`
    : undefined;

  return (
    <FieldError error={error}>
      <RadixPopover.Root
        open={isPopoverOpen}
        onOpenChange={handleOpenChange}
        modal={modalPopover}
      >
        <RadixPopover.Trigger asChild>
          <button
            type="button"
            onClick={handleTogglePopover}
            className={clsx(
              selectStyles.selectTrigger,
              selectStyles.selectTriggerSizes[size],
              isLabeled && [
                labeledStyles.labeledTrigger,
                labeledStyles.labeledTriggerSizes[size]
              ],
              error && "inputError"
            )}
            disabled={disabled}
            data-disabled={disabled || undefined}
            aria-expanded={isPopoverOpen}
            aria-invalid={error ? true : undefined}
            onKeyDown={handleTriggerKeyDown}
            aria-label={triggerAriaLabel}
          >
            {isLabeled ? (
              <LabeledTriggerValue
                label={label}
                selectedValues={value || []}
                options={options}
                placeholder={placeholder}
                size={size}
              />
            ) : (
              <MultiTriggerValue
                selectedValues={value || []}
                options={options}
                toggleOption={toggleOption}
                placeholder={placeholder}
                disabled={disabled}
              />
            )}
            <div className={selectStyles.selectButtonLayout}>
              {triggerActions && (
                <div
                  className={selectStyles.triggerActionsContainer}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {triggerActions}
                </div>
              )}
              {/* A disabled select offers no action: no clear, no chevron. */}
              {isActive && !disabled && (
                <>
                  <span
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                    className={selectStyles.closeButtonWrapper}
                    aria-label={isLabeled ? `Clear ${label}` : undefined}
                    role={isLabeled ? "button" : undefined}
                  >
                    <Icon name="close" />
                  </span>
                  <div className={selectStyles.separator}>
                    <Divider orientation="vertical" />
                  </div>
                </>
              )}
              {!disabled &&
                (isLabeled ? (
                  <div className={selectStyles.chevronWrapper}>
                    <Icon
                      name={isPopoverOpen ? "chevron-up" : "chevron-down"}
                    />
                  </div>
                ) : (
                  <Icon name={isPopoverOpen ? "chevron-up" : "chevron-down"} />
                ))}
            </div>
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side={position}
            sideOffset={5}
            className={clsx(
              selectStyles.selectPopover,
              selectStyles.selectPortalRoot
            )}
            onEscapeKeyDown={() => handleOpenChange(false)}
            onKeyDownCapture={handleKeyDown}
            role="dialog"
            aria-label={isLabeled ? `${label} filter` : "Select dropdown"}
          >
            {/* CMDK filtering is OFF to support virtualization. The highlight
                is owned by useSelectKeyboard (data-active / aria-activedescendant);
                cmdk's own `aria-selected` highlight is left unstyled. */}
            <Command shouldFilter={false}>
              {searchable && !loading && (
                <div className={selectStyles.selectSearchWrapper}>
                  <Icon name="search" />
                  <Command.Input
                    className={selectStyles.selectSearchInput}
                    placeholder={SEARCH_TEXT}
                    autoFocus
                    value={search}
                    onValueChange={(v) => {
                      setSearch(v);
                      setPasteReport(null);
                    }}
                    onPaste={handleSearchPaste}
                    aria-controls={optionListId}
                    aria-activedescendant={activeDescendantId}
                  />
                </div>
              )}

              {pasteReport && (
                <Note
                  className={selectStyles.pasteReport}
                  size="compact"
                  text={pasteReport}
                  role="status"
                />
              )}

              {showSelectHeaderActions && (
                <SelectHeaderActions
                  value={value}
                  filteredOptions={sortedOptions}
                  onChange={onChange}
                  size={size}
                />
              )}

              <Command.List
                ref={listRef}
                id={optionListId}
                className={selectStyles.selectList}
                role="listbox"
                aria-multiselectable
                style={
                  loading || sortedOptions.length === 0 ? viewPortStyle : {}
                }
              >
                {loading ? (
                  <Command.Loading
                    className={clsx(
                      selectStyles.selectListSecondaryState,
                      selectStyles.selectListSecondaryStateSizes[size]
                    )}
                  >
                    {LOADING_TEXT}
                  </Command.Loading>
                ) : sortedOptions.length === 0 ? (
                  <div
                    className={clsx(
                      selectStyles.selectListSecondaryState,
                      selectStyles.selectListSecondaryStateSizes[size]
                    )}
                  >
                    {NO_RESULTS_TEXT}
                  </div>
                ) : (
                  <Command.Group>
                    <div
                      style={{
                        height: `${virtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative"
                      }}
                    >
                      {virtualItems.map((vi) => {
                        const opt = sortedOptions[vi.index];
                        const isSelected = value
                          ? value.includes(opt.value)
                          : false;
                        const isRowActive = vi.index === activeIndex;

                        return (
                          <React.Fragment key={opt.value}>
                            <Command.Item
                              id={`${optionListId}-${opt.value}`}
                              value={opt.value}
                              ref={virtualizer.measureElement}
                              data-index={vi.index}
                              data-active={isRowActive ? "true" : undefined}
                              className={clsx(
                                selectStyles.selectItem,
                                selectStyles.selectItemSizes[size],
                                selectStyles.virtualizedItem
                              )}
                              role="option"
                              aria-selected={isSelected}
                              aria-disabled={opt.disabled}
                              tabIndex={-1}
                              onSelect={() => toggleOption(opt.value)}
                              style={{
                                height: `${vi.size}px`,
                                transform: `translateY(${vi.start}px)`
                              }}
                            >
                              <Checkbox
                                variant={
                                  opt.relevant === false ? "ghost" : "primary"
                                }
                                checked={isSelected}
                                disabled={opt.disabled}
                                onChange={() => toggleOption(opt.value)}
                              />
                              <span
                                className={selectStyles.optionLabel}
                                data-relevant={
                                  opt.relevant === false ? "false" : undefined
                                }
                              >
                                {opt.label}
                              </span>
                            </Command.Item>

                            {vi.index === dividerIndex && (
                              <div
                                key="divider"
                                className={selectStyles.relevanceDivider}
                                style={{ top: `${vi.start + vi.size}px` }}
                              >
                                <Divider />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </Command.Group>
                )}
              </Command.List>
              {resolvedFooter && (
                <div className={selectStyles.selectFooterAction}>
                  {resolvedFooter}
                </div>
              )}
              {!loading && sortedOptions.length > 0 && !resolvedFooter && (
                <SelectFooterActions
                  clearDisabled={!value?.length}
                  onClear={handleClear}
                  onClose={() => setIsPopoverOpen(false)}
                />
              )}
            </Command>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </FieldError>
  );
}
